// Add Product side panel for System Configuration > Product & Feature
// Configuration. Split out of the previously-combined SupportAdmin/index.js
// (2026-09-09) into its own file, alongside EditProduct.js (which no longer
// shows the Select Product dropdown -- the product being edited is already
// fixed by which row you clicked) and shared.js (the pieces identical
// between the two panels).
import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { GlobalFieldLabel } from "components/DesignSystem/sharedUiPatterns";
import { customAPI } from "services/utils/CustomAPI";
import { notifyAccessChanged } from "services/utils/companyAccess";
import { useFetch } from "services/hooks/useFetch";
import { showToast } from "services/utils/Status";
import { PANEL_DRAWER_WIDTH } from "services/constants/designTokens";
import {
  ACCESS_DATE_MAX,
  ACCESS_DATE_MIN,
  ACCESS_FIELD_DEFAULTS,
  ACCESS_TYPE_API_VALUES,
  AccessValiditySection,
  ProductFeatureStatCards,
  ProductFeaturesSection,
  SaveConfigurationFooter,
  addon,
  isEffectivelyActive,
} from "./shared";

// `productCatalog` is passed down from the List view's own already-loaded
// product-catalog/ fetch (see index.js) rather than fetched again here, so
// opening this panel doesn't trigger a redundant network call for data the
// page already has.
export const AddProduct = ({
  open,
  onClose,
  productCatalog,
  configuredProductIds = [],
  onRefresh,
}) => {
  // Product/feature configuration is schema-wide, not per-company, so no
  // company_id is read from the topbar or sent to the backend.
  const { domain } = useSelector((state) => state.tokenReducer);

  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [productError, setProductError] = useState(false);
  const [dateError, setDateError] = useState({});
  const [featureSearch, setFeatureSearch] = useState("");

  // Reset to a blank slate every time the panel is (re)opened, mirroring the
  // combined screen's old openPanel() reset.
  useEffect(() => {
    if (open) {
      setSelectedId("");
      setFeatureSearch("");
      setProductError(false);
      setDateError({});
    }
  }, [open]);

  // Products this company already has a configuration for are edited from
  // the list, so they shouldn't be offered again as "new" here.
  const availableProducts = productCatalog.filter(
    (item) =>
      !configuredProductIds.some((id) => String(id) === String(item.id)),
  );

  const catalogMatch = selectedId
    ? productCatalog.find((item) => item.id === selectedId) || null
    : null;

  // Fired the moment a product is picked -- returns that one product's own
  // core/add-on feature detail (query-filtered list, not a path segment).
  const { result: selectedProductDetailResult = [] } = useFetch(
    selectedId
      ? `${domain}product-feature-config/products/?product_id=${selectedId}`
      : "",
  );
  const selectedProductDetail = Array.isArray(selectedProductDetailResult)
    ? selectedProductDetailResult.find((item) => item.id === selectedId) ||
      selectedProductDetailResult[0] ||
      null
    : selectedProductDetailResult || null;

  const [productFeatures, setProductFeatures] = useState([]);
  useEffect(() => {
    if (!selectedId) {
      setProductFeatures([]);
      return;
    }
    // Prefer the freshly-fetched per-product detail; fall back to the
    // already-loaded catalog entry so the panel isn't empty while that
    // request is still in flight.
    const detail = selectedProductDetail || catalogMatch;
    const basicList = Array.isArray(detail?.basic_features)
      ? detail.basic_features
      : [];
    const addonList = Array.isArray(detail?.addon_features)
      ? detail.addon_features
      : [];
    setProductFeatures([...basicList, ...addonList]);
  }, [selectedId, selectedProductDetail, catalogMatch]);

  const [selectedAccess, setSelectedAccess] = useState(null);
  useEffect(() => {
    if (!selectedId || !catalogMatch) {
      setSelectedAccess(null);
      return;
    }
    setSelectedAccess({
      access_type: catalogMatch.access_type ?? ACCESS_FIELD_DEFAULTS.access_type,
      start_date: catalogMatch.start_date ?? ACCESS_FIELD_DEFAULTS.start_date,
      access_until:
        catalogMatch.access_until ?? ACCESS_FIELD_DEFAULTS.access_until,
      auto_expire:
        catalogMatch.auto_expire !== undefined
          ? catalogMatch.auto_expire
          : ACCESS_FIELD_DEFAULTS.auto_expire,
    });
  }, [selectedId, catalogMatch]);

  const selected = catalogMatch && {
    ...ACCESS_FIELD_DEFAULTS,
    ...catalogMatch,
    ...(selectedProductDetail || {}),
    ...(selectedAccess || {}),
  };

  const productBasicFeatures = productFeatures.filter((item) => !addon(item));
  const productAddonFeatures = productFeatures.filter(addon);

  const updateProductField = (field, value) =>
    setSelectedAccess((prev) => (prev ? { ...prev, [field]: value } : prev));
  const updateProductFeature = (id, value) =>
    setProductFeatures((items) =>
      items.map((item) =>
        item.id === id ? { ...item, is_active: value } : item,
      ),
    );

  const close = () => {
    onClose();
  };

  const save = async () => {
    if (!selected) return setProductError(true);

    if (selected.access_type !== "Internal") {
      const errors = {};
      if (!selected.start_date) errors.start_date = "Start date is required.";
      if (!selected.access_until)
        errors.access_until = "Access until date is required.";
      if (
        selected.start_date &&
        (!dayjs(selected.start_date).isValid() ||
          dayjs(selected.start_date).isBefore(ACCESS_DATE_MIN, "day") ||
          dayjs(selected.start_date).isAfter(ACCESS_DATE_MAX, "day"))
      ) {
        errors.start_date = "Enter a valid start date.";
      }
      if (
        selected.access_until &&
        (!dayjs(selected.access_until).isValid() ||
          dayjs(selected.access_until).isBefore(ACCESS_DATE_MIN, "day") ||
          dayjs(selected.access_until).isAfter(ACCESS_DATE_MAX, "day"))
      ) {
        errors.access_until = "Enter a valid access until date.";
      }
      if (
        !errors.start_date &&
        !errors.access_until &&
        selected.start_date &&
        selected.access_until &&
        dayjs(selected.access_until).isBefore(dayjs(selected.start_date), "day")
      ) {
        errors.start_date = "Start date must be before the access until date.";
        errors.access_until =
          "Access until date can't be before the start date.";
      }
      if (Object.keys(errors).length) {
        setDateError(errors);
        return;
      }
    }
    setSaving(true);
    try {
      // Add Product always creates this company's first record for the
      // product, so it's always a POST (unlike Edit, which PUTs an existing
      // row and has to send that row's own id back -- see EditProduct.js).
      const result = await customAPI(
        `${domain}product/access/company-features/`,
        "POST",
        {
          product_id: selectedId,
          access_type:
            ACCESS_TYPE_API_VALUES[selected.access_type] ||
            ACCESS_TYPE_API_VALUES[ACCESS_FIELD_DEFAULTS.access_type],
          is_active: isEffectivelyActive(selected),
          ...(selected.access_type !== "Internal" && {
            start_date: selected.start_date || null,
            end_date: selected.access_until || null,
            auto_expire: Boolean(selected.auto_expire),
          }),
          features: productFeatures.map((item) => ({
            feature_id: item.feature_id ?? item.id,
            is_active: Boolean(item.is_active),
          })),
        },
      );
      if (result.ok) {
        notifyAccessChanged();
        showToast("success", "Product and feature configuration saved.");
        onRefresh();
        close();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={close}
      title="Add Product"
      subtitle="Enable or disable features for the selected product."
      breadcrumb={["System Configuration", "Product & Feature Configuration"]}
      width={PANEL_DRAWER_WIDTH}
      footer={
        <SaveConfigurationFooter
          onCancel={close}
          onSave={save}
          saving={saving}
        />
      }
    >
      <Mui.Stack spacing={3} sx={{ pt: 2.5, px: 3, pb: 2.5 }}>
        <Mui.Box>
          <GlobalFieldLabel helperText="Choose which product to configure access and features for.">
            Select Product
          </GlobalFieldLabel>
          <CustomAutoComplete
            dataList={availableProducts.map((item) => ({
              ...item,
              label: item.name,
            }))}
            selectedValue={selected?.name || ""}
            updateDataFunction={(event, value) => {
              setSelectedId(value?.id || "");
              if (value) setProductError(false);
            }}
            placeHolder="Search and select a product"
            errorMessage={productError ? "Select a product to continue." : ""}
            textBoxError={productError}
          />
        </Mui.Box>
        <ProductFeatureStatCards
          total={productFeatures.length}
          basic={productBasicFeatures.length}
          addons={productAddonFeatures.length}
        />
        <AccessValiditySection
          selected={selected}
          dateError={dateError}
          setDateError={setDateError}
          onFieldChange={updateProductField}
        />
        <ProductFeaturesSection
          featureSearch={featureSearch}
          setFeatureSearch={setFeatureSearch}
          basicFeatures={productBasicFeatures}
          addonFeatures={productAddonFeatures}
          onToggleFeature={updateProductFeature}
        />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
